import React, { useEffect, useState } from "react";
import { useI18n } from "@/context/I18nContext";
import { IdentificationCard, ShieldStar, QrCode, CheckCircle } from "@phosphor-icons/react";

/**
 * Auto-playing 3-scene walkthrough embedded on the landing page. Reproduces
 * the actual UTAL MEA UI in a compressed timeline (~15s) so first-time
 * visitors immediately see the full workflow, in EN / AR (RTL) / FR.
 */

const STRINGS = {
    en: {
        kicker: "· LIVE DEMO",
        heading: "See UTAL MEA in action",
        s1_short: "01 · REGISTER",
        s2_short: "02 · SIGN & ISSUE",
        s3_short: "03 · FIELD SCAN",
        newApplication: "· NEW APPLICATION",
        vehicleReg: "Vehicle registration",
        owner: "Owner",
        plate: "Plate #",
        vin: "VIN",
        region: "Region",
        make: "Make",
        model: "Model",
        year: "Year",
        fuel: "Fuel",
        petrol: "petrol",
        submit: "Submit for approval",
        workflow: "workflow",
        wRegistrar: "Registrar files",
        wApprover: "Approver reviews",
        wIssued: "Sticker issued",
        signed: "· ED25519 SIGNED",
        header: "Header",
        payload: "Payload",
        signature: "Signature",
        sigVal: "Ed25519 · 64 bytes",
        issued: "Issued",
        readyToPrint: "Ready to print · Zebra ZPL",
        result: "Result",
        valid: "VALID",
        vehicle: "Vehicle",
        expires: "Expires",
        sigVerified: "Signature verified · Ed25519 · Offline OK",
        scanTag: "SCAN · 30 FPS · OFFLINE",
        ownerName: "Jane Al-Karim",
        vehicleFull: "Toyota Camry 2023",
        vehicleOwnerLine: "Toyota Camry 2023 · Jane Al-Karim",
    },
    ar: {
        kicker: "· عرض مباشر",
        heading: "شاهد UTAL MEA أثناء التشغيل",
        s1_short: "01 · تسجيل",
        s2_short: "02 · توقيع وإصدار",
        s3_short: "03 · مسح ميداني",
        newApplication: "· طلب جديد",
        vehicleReg: "تسجيل المركبة",
        owner: "المالك",
        plate: "رقم اللوحة",
        vin: "رقم الهيكل",
        region: "المنطقة",
        make: "الصانع",
        model: "الطراز",
        year: "السنة",
        fuel: "الوقود",
        petrol: "بنزين",
        submit: "إرسال للموافقة",
        workflow: "سير العمل",
        wRegistrar: "المُسجِّل يفتح الطلب",
        wApprover: "المُوافِق يراجع",
        wIssued: "تم إصدار الملصق",
        signed: "· موقّع بـ ED25519",
        header: "الترويسة",
        payload: "الحمولة",
        signature: "التوقيع",
        sigVal: "Ed25519 · 64 بايت",
        issued: "تم الإصدار",
        readyToPrint: "جاهز للطباعة · Zebra ZPL",
        result: "النتيجة",
        valid: "صالح",
        vehicle: "المركبة",
        expires: "تنتهي في",
        sigVerified: "تم التحقق من التوقيع · Ed25519 · يعمل دون اتصال",
        scanTag: "مسح · 30 إطار/ث · دون اتصال",
        ownerName: "جين الكريم",
        vehicleFull: "تويوتا كامري 2023",
        vehicleOwnerLine: "تويوتا كامري 2023 · جين الكريم",
    },
    fr: {
        kicker: "· DÉMO EN DIRECT",
        heading: "UTAL MEA en action",
        s1_short: "01 · ENREGISTREMENT",
        s2_short: "02 · SIGNATURE & ÉMISSION",
        s3_short: "03 · SCAN SUR LE TERRAIN",
        newApplication: "· NOUVELLE DEMANDE",
        vehicleReg: "Immatriculation du véhicule",
        owner: "Propriétaire",
        plate: "N° de plaque",
        vin: "NIV",
        region: "Région",
        make: "Marque",
        model: "Modèle",
        year: "Année",
        fuel: "Carburant",
        petrol: "essence",
        submit: "Soumettre pour approbation",
        workflow: "flux",
        wRegistrar: "L'agent ouvre le dossier",
        wApprover: "L'approbateur vérifie",
        wIssued: "Vignette émise",
        signed: "· SIGNÉ ED25519",
        header: "En-tête",
        payload: "Charge utile",
        signature: "Signature",
        sigVal: "Ed25519 · 64 octets",
        issued: "Émise",
        readyToPrint: "Prête à imprimer · Zebra ZPL",
        result: "Résultat",
        valid: "VALIDE",
        vehicle: "Véhicule",
        expires: "Expire",
        sigVerified: "Signature vérifiée · Ed25519 · Hors ligne OK",
        scanTag: "SCAN · 30 IPS · HORS LIGNE",
        ownerName: "Jane Al-Karim",
        vehicleFull: "Toyota Camry 2023",
        vehicleOwnerLine: "Toyota Camry 2023 · Jane Al-Karim",
    },
};

const SCENE_MS = 5000;

export default function LandingWalkthrough() {
    const { lang } = useI18n();
    const s = STRINGS[lang] || STRINGS.en;
    const scenes = [
        { key: "register", label: s.s1_short, icon: IdentificationCard },
        { key: "sign",     label: s.s2_short, icon: ShieldStar },
        { key: "scan",     label: s.s3_short, icon: QrCode },
    ];
    const [i, setI] = useState(0);
    useEffect(() => {
        const t = setInterval(() => setI((v) => (v + 1) % scenes.length), SCENE_MS);
        return () => clearInterval(t);
    }, [scenes.length]);

    return (
        <section className="relative z-10 max-w-7xl mx-auto px-6 sm:px-12 pb-24" data-testid="landing-walkthrough">
            <div className="mb-8 flex items-center justify-between flex-wrap gap-4">
                <div>
                    <div className="text-[10px] tracking-[0.35em] uppercase text-primary font-bold">{s.kicker}</div>
                    <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mt-1">{s.heading}</h2>
                </div>
                <div className="flex gap-1.5" data-testid="wt-timeline">
                    {scenes.map((sc, idx) => (
                        <button
                            key={sc.key}
                            type="button"
                            onClick={() => setI(idx)}
                            className={`h-1 rounded-full transition-all ${idx === i ? "w-16 bg-primary" : "w-6 bg-white/20 hover:bg-white/40"}`}
                            aria-label={`Jump to scene ${idx + 1}`}
                        />
                    ))}
                </div>
            </div>

            <div className="relative aspect-[16/9] bg-[#0b0b0d] border border-white/10 rounded overflow-hidden shadow-2xl" data-testid="wt-stage">
                {/* Chrome bar */}
                <div className="absolute top-0 left-0 right-0 h-8 bg-black/60 border-b border-white/5 flex items-center gap-2 px-3 z-20" dir="ltr">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
                    <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/70" />
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/70" />
                    <div className="ml-3 text-[10px] mono text-white/50">utal-mea.com/{scenes[i].key}</div>
                    <div className="ml-auto flex items-center gap-1.5">
                        {scenes.map((sc, idx) => {
                            const Icon = sc.icon;
                            const active = idx === i;
                            return (
                                <div key={sc.key} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[9px] tracking-[0.2em] uppercase font-bold transition-all ${active ? "bg-primary/20 text-primary" : "text-white/30"}`}>
                                    <Icon size={10} weight={active ? "fill" : "bold"} />{sc.label}
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="absolute inset-0 pt-8">
                    {i === 0 && <SceneRegister s={s} />}
                    {i === 1 && <SceneSign s={s} />}
                    {i === 2 && <SceneScan s={s} />}
                </div>
            </div>
        </section>
    );
}

/* -------- Scene 1: Data entry -------- */
function SceneRegister({ s }) {
    const owner = useTypewriter(s.ownerName, 40);
    const plate = useTypewriter("DEMO-GRN-001", 60, 400);
    const vin = useTypewriter("1HGBH41JXMN109186", 30, 900);
    const make = useTypewriter(s.vehicleFull.split(" ")[0], 60, 1900);
    const model = useTypewriter(s.vehicleFull.split(" ")[1] || "Camry", 60, 2200);
    const year = useTypewriter("2023", 100, 2500);

    return (
        <div className="w-full h-full grid grid-cols-[1fr_360px] gap-6 p-6 text-white" data-testid="scene-register">
            <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-primary font-bold">{s.newApplication}</div>
                <h3 className="text-xl font-bold mt-1 mb-5">{s.vehicleReg}</h3>

                <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                    <Field label={s.owner} value={owner} />
                    <Field label={s.plate} value={plate} mono />
                    <Field label={s.vin} value={vin} mono />
                    <Field label={s.region} value={owner ? "A" : ""} />
                    <Field label={s.make} value={make} />
                    <Field label={s.model} value={model} />
                    <Field label={s.year} value={year} />
                    <Field label={s.fuel} value={year ? s.petrol : ""} />
                </div>

                <div className="mt-6 flex gap-2">
                    <div className="h-8 px-3 flex items-center bg-primary text-white text-[11px] tracking-widest uppercase font-bold rounded-sm">
                        {s.submit}
                    </div>
                </div>
            </div>

            <aside className="border-l border-white/10 pl-6">
                <div className="text-[9px] tracking-[0.3em] uppercase text-white/40 mb-3 font-bold">{s.workflow}</div>
                <ol className="space-y-2 text-xs">
                    <Step done label={s.wRegistrar} />
                    <Step done={!!year} label={s.wApprover} delay={2600} />
                    <Step label={s.wIssued} />
                </ol>
            </aside>
        </div>
    );
}

/* -------- Scene 2: Sign & Issue -------- */
function SceneSign({ s }) {
    return (
        <div className="w-full h-full flex items-center justify-center p-6 text-white" data-testid="scene-sign">
            <div className="grid grid-cols-[220px_1fr] gap-8 items-center max-w-3xl w-full">
                <div className="aspect-square bg-white p-3 rounded-sm animate-[fadeIn_0.5s_ease-out]">
                    <QrPattern />
                </div>
                <div>
                    <div className="text-[10px] tracking-[0.3em] uppercase text-primary font-bold animate-[slideUp_0.4s_ease-out_0.2s_both]">{s.signed}</div>
                    <div className="text-2xl font-black mono mt-1 animate-[slideUp_0.4s_ease-out_0.4s_both]" dir="ltr">DEMO-GRN-001</div>
                    <div className="text-xs text-white/60 mt-1 animate-[slideUp_0.4s_ease-out_0.6s_both]">{s.vehicleOwnerLine}</div>

                    <div className="mt-5 space-y-2 text-xs">
                        <SignRow label={s.header} value="{alg:EdDSA, typ:PLATE, kid:utal-…3b881c}" delay={800} />
                        <SignRow label={s.payload} value="{plate, vin, exp, iat, iss:UTAL-MEA…}" delay={1100} />
                        <SignRow label={s.signature} value={s.sigVal} delay={1400} accent />
                    </div>

                    <div className="mt-5 flex gap-3 items-center animate-[slideUp_0.4s_ease-out_1.8s_both]">
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-[10px] tracking-widest uppercase font-bold rounded-sm">
                            <CheckCircle size={11} weight="fill" /> {s.issued}
                        </div>
                        <div className="text-[10px] tracking-[0.25em] uppercase text-white/40">{s.readyToPrint}</div>
                    </div>
                </div>
            </div>
        </div>
    );
}

/* -------- Scene 3: Field scan -------- */
function SceneScan({ s }) {
    return (
        <div className="w-full h-full flex items-center justify-center p-6 text-white" data-testid="scene-scan">
            <div className="grid grid-cols-[220px_1fr] gap-8 items-center max-w-3xl w-full">
                <div className="relative aspect-[9/16] bg-black border border-white/10 rounded-lg overflow-hidden max-h-[280px] mx-auto">
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="absolute top-3 left-3 w-6 h-6 border-l-2 border-t-2 border-emerald-500" />
                        <div className="absolute top-3 right-3 w-6 h-6 border-r-2 border-t-2 border-emerald-500" />
                        <div className="absolute bottom-3 left-3 w-6 h-6 border-l-2 border-b-2 border-emerald-500" />
                        <div className="absolute bottom-3 right-3 w-6 h-6 border-r-2 border-b-2 border-emerald-500" />
                        <div className="scale-75 opacity-90"><QrPattern /></div>
                        <div className="absolute left-0 right-0 h-0.5 bg-emerald-400/80 animate-[scanLine_1.5s_ease-in-out_infinite]" style={{ boxShadow: "0 0 12px rgba(52,211,153,0.7)" }} />
                    </div>
                    <div className="absolute bottom-1 left-0 right-0 text-center text-[9px] mono text-emerald-400/80">{s.scanTag}</div>
                </div>

                <div className="animate-[slideUp_0.4s_ease-out_1.4s_both]">
                    <div className="px-5 py-4 rounded bg-emerald-500 text-white flex items-center justify-between">
                        <div>
                            <div className="text-[9px] tracking-[0.35em] uppercase font-bold opacity-80">{s.result}</div>
                            <div className="text-3xl font-black tracking-widest">{s.valid}</div>
                        </div>
                        <CheckCircle size={40} weight="fill" />
                    </div>
                    <div className="mt-4 space-y-1.5 text-xs">
                        <ResultRow label={s.plate} value="DEMO-GRN-001" mono />
                        <ResultRow label={s.owner} value={s.ownerName} />
                        <ResultRow label={s.vin} value="1HGBH41JXMN109186" mono />
                        <ResultRow label={s.vehicle} value={s.vehicleFull} />
                        <ResultRow label={s.region} value="A" />
                        <ResultRow label={s.expires} value="2029-08-23" mono />
                    </div>
                    <div className="mt-3 text-[10px] tracking-[0.25em] uppercase text-white/40">{s.sigVerified}</div>
                </div>
            </div>
        </div>
    );
}

/* -------- primitives -------- */
function Field({ label, value, mono }) {
    return (
        <div>
            <div className="text-[9px] tracking-[0.25em] uppercase text-white/40 font-bold">{label}</div>
            <div className={`h-8 mt-1 border-b border-white/20 flex items-center ${mono ? "mono" : ""} text-sm`}>
                {value}
                {value && <span className="ml-0.5 inline-block w-0.5 h-4 bg-primary animate-pulse" />}
            </div>
        </div>
    );
}

function Step({ done, label, delay = 0 }) {
    return (
        <li className="flex items-center gap-2" style={done ? { animation: `slideUp .3s ease-out ${delay}ms both` } : undefined}>
            <span className={`w-2 h-2 rounded-full transition-colors ${done ? "bg-emerald-500" : "bg-white/20"}`} />
            <span className={done ? "text-white" : "text-white/40"}>{label}</span>
        </li>
    );
}

function SignRow({ label, value, delay, accent }) {
    return (
        <div className="flex items-baseline gap-2 border-b border-white/5 pb-1.5" style={{ animation: `slideUp .3s ease-out ${delay}ms both` }}>
            <span className="text-[9px] tracking-[0.25em] uppercase text-white/40 w-20 font-bold shrink-0">{label}</span>
            <span className={`mono text-[11px] flex-1 truncate ${accent ? "text-primary" : "text-white/80"}`} dir="ltr">{value}</span>
        </div>
    );
}

function ResultRow({ label, value, mono }) {
    return (
        <div className="flex items-center justify-between border-b border-white/5 pb-1">
            <span className="text-[9px] tracking-[0.25em] uppercase text-white/40 font-bold">{label}</span>
            <span className={`text-white ${mono ? "mono" : ""} text-xs`} dir={mono ? "ltr" : undefined}>{value}</span>
        </div>
    );
}

function QrPattern() {
    const size = 21;
    const seed = 42;
    const cells = [];
    let x = seed;
    for (let r = 0; r < size; r++) {
        for (let c = 0; c < size; c++) {
            x = (x * 1103515245 + 12345) & 0x7fffffff;
            const isFinder = (r < 7 && c < 7) || (r < 7 && c >= size - 7) || (r >= size - 7 && c < 7);
            const on = isFinder ? finderOn(r, c, size) : (x & 1) === 1;
            cells.push(on);
        }
    }
    return (
        <div className="w-full h-full grid" style={{ gridTemplateColumns: `repeat(${size}, 1fr)`, gridTemplateRows: `repeat(${size}, 1fr)` }} dir="ltr">
            {cells.map((on, k) => (
                <div key={k} className={on ? "bg-black" : "bg-white"} />
            ))}
        </div>
    );
}
function finderOn(r, c, size) {
    const inCorner = (rr, cc) => {
        const rel_r = r - rr, rel_c = c - cc;
        if (rel_r < 0 || rel_c < 0 || rel_r > 6 || rel_c > 6) return false;
        if (rel_r === 0 || rel_r === 6 || rel_c === 0 || rel_c === 6) return true;
        if (rel_r >= 2 && rel_r <= 4 && rel_c >= 2 && rel_c <= 4) return true;
        return false;
    };
    return inCorner(0, 0) || inCorner(0, size - 7) || inCorner(size - 7, 0);
}

function useTypewriter(target, speed = 50, startAfter = 0) {
    const [out, setOut] = useState("");
    useEffect(() => {
        setOut("");
        let i = 0;
        const start = setTimeout(() => {
            const tick = setInterval(() => {
                i++;
                setOut(target.slice(0, i));
                if (i >= target.length) clearInterval(tick);
            }, speed);
            return () => clearInterval(tick);
        }, startAfter);
        return () => clearTimeout(start);
    }, [target, speed, startAfter]);
    return out;
}
